/**
 * Campaigns copy (DD-027). One object, one formatter per concern.
 *
 * Commit labels are `<Verb> <Noun>` Title Case; dismiss is Cancel before the run
 * and Close after it; errors are "Could not <verb> <noun>. <recovery>"; empty
 * states are sentence case with a next action.
 */
export const CAMPAIGNS_COPY = {
  title: 'Campaigns',
  newCampaign: 'New Campaign',
  list: {
    empty: 'No campaigns yet. Start one to contact a group of households.',
    columns: 'Columns',
    export: 'Export',
    clearFilters: 'Clear',
    results: (count: number): string => `Results: ${count}`,
    reach: 'Reach',
    state: 'State',
    name: 'Campaign',
    // ENG-4289: "date sent" is the started_at stamp — the moment sending began.
    dateSent: 'Date sent',
    sentBy: 'Who sent',
  },
  wizard: {
    title: 'New campaign',
    editTitle: 'Edit campaign',
    steps: ['Audience', 'Channels', 'Content', 'Review', 'Start'],
    next: 'Continue',
    back: 'Back',
    // ENG-4289: the terminal label. "Send Campaign" is retired — the fifth step
    // is a commitment page, and the button names what it starts.
    start: 'Start Campaign',
    startTitle: 'Start campaign',
    startLead:
      'Starting sends this campaign now. Nothing has been sent yet, and it cannot be recalled once it begins.',
    startResults:
      'Each recipient is tried in turn. Their outcome — reached, not reached, or excluded — appears on the run screen that opens next.',
    startDeliveryOrder:
      'Channels are tried in the order shown on Review. When one cannot be used, the next one is tried.',
    startDeliveryEvery:
      'Every planned channel is used for every household, even when an earlier one worked.',
    saveDraft: 'Save Draft',
    nameLabel: 'Campaign name',
    audienceTitle: 'Who should this reach?',
    recipientRule: 'Send to',
    patGates: 'Only contact households the reminder rules allow',
    patGatesHelp:
      'Leaves out households reminded too recently, households already at their message limit, households with notifications turned off, and households on a status where reminders are off. Each one is listed under Excluded with its reason.',
    countTotal: 'Households matched',
    countIncluded: 'Will be contacted',
    countExcluded: 'Excluded',
    reachable: 'Reachable',
    complianceFilters: 'Quick Filters',
    contentTitle: 'What should it say?',
    templateLabel: 'Template',
    emailTemplateLabel: 'Email template',
    emailTemplateHelp:
      'Pick a saved template to fill the subject and body. Both stay editable for this campaign only.',
    smsTemplateLabel: 'SMS template',
    smsTemplateHelp:
      'Pick a saved SMS template, then edit it for this campaign if you need to.',
    noTemplates: 'No templates available for this property',
    mergeFieldsHelp:
      'Merge fields are filled per recipient: {{ USER_NAME }}, {{ PROPERTY_NAME }}, {{ AUTH_LINK }}, {{ DEADLINE_DATE }}.',
    subjectLabel: 'Email subject',
    emailLabel: 'Email body',
    smsLabel: 'SMS body',
    voiceGoal: 'Call goal',
    voiceLanguage: 'Call language',
    voiceGoalHelpLabel: 'What is a call goal?',
    voiceLanguageNote:
      "Language follows the household: the member's own preference (English, Spanish or Chinese), then the head of household's, then English. It is not set per campaign.",
    voiceGoalHelp: {
      title: 'What the call goal does',
      what: 'The goal tells the AI voice agent why we are calling before it dials. The agent answers the phone, says it is calling from the property, and confirms it is speaking to the right person before it discusses anything. The goal is how it knows which part of the file to talk about.',
      optionsTitle: 'The goals',
      options: [
        {
          goal: 'Status inquiry',
          meaning: 'where the application stands right now.',
        },
        {
          goal: 'Missing documents',
          meaning:
            'which documents are still outstanding, and how to upload them.',
        },
        {
          goal: 'Signature reminder',
          meaning: "forms waiting for the renter's signature.",
        },
        {
          goal: 'Appointment',
          meaning:
            'an upcoming appointment — confirming it or taking a reschedule request.',
        },
        {
          goal: 'Deadline reminder',
          meaning:
            'the deadline date on the file and what is left to do before it.',
        },
        {
          goal: 'Callback',
          meaning: 'we promised to call this household back.',
        },
        {
          goal: 'General',
          meaning: 'an open conversation with no single subject.',
        },
        {
          goal: 'Escalation follow-up',
          meaning:
            'a follow-up after the file was escalated to the property team.',
        },
      ],
      canTitle: 'What the agent can do on the call',
      can: [
        'Confirm who picked up before saying anything about the file — an outbound call starts unidentified.',
        'Tell the renter what is outstanding: documents, signatures, and the deadline on their file.',
        'Explain how to get back into the application and where to upload.',
        'Answer questions about the property and the process.',
        'Record a callback request, which becomes a task for the compliance team.',
      ],
      cannotTitle: 'What it does not do',
      cannot: [
        'It does not approve, deny, or change a household, unit or status.',
        'It does not take SSNs, bank details or documents over the phone.',
        'There is no human queue on an outbound call: if the renter needs a specialist, the agent records it for a callback instead.',
        'The outcome appears on the run screen as Connected, Not answered, or Connected but identity not confirmed.',
      ],
    },
    attachments: 'Attachments',
    planTitle: 'How should it go out?',
    planHelp:
      'Pick the channels and the order. One choice decides what happens after a channel succeeds and after it fails, so the plan cannot contradict itself. Next you will write the copy for the channels you picked.',
    planMode: 'How should the channels be used?',
    modeInOrder: 'Try channels in order',
    modeInOrderHelp:
      'Use the first channel that can reach the household. The rest are only tried when it fails.',
    modeEvery: 'Send every channel',
    modeEveryHelp:
      'Contact the household on every channel listed, whether or not an earlier one worked.',
    planReach: (people: number, households: number): string =>
      `${people} ${people === 1 ? 'person' : 'people'} across ${households} ${
        households === 1 ? 'household' : 'households'
      } will be contacted with this plan.`,
    planReachEmpty: 'Choose a channel to see who this plan reaches.',
    addChannel: 'Add channel',
    planEmpty: 'A campaign needs at least one channel.',
    reviewTitle: 'Review',
    scheduledFor: 'Send at',
    sendNow: 'Send now',
    noRecipients: 'No recipients match these filters.',
    previewing: 'Counting…',
    // CAMP-002 — the card must say which gate setting produced its numbers.
    gatesRespected: 'Reminder rules applied',
    gatesIgnored: 'Reminder rules not applied',
    excludedTitle: 'Why households are out',
    gatesStale: 'The form changed — recounting.',
    channelContent: (channel: string): string => `${channel} content`,
    channelNotPlanned: 'This channel is not in the plan.',
    templateEdited: 'Edited since the template was applied.',
    templateNone: 'None — write your own',
    templateSearch: 'Type to filter templates',
    templateSystemGroup: 'System notifications',
    templateOtherGroup: 'Other templates',
    templateSystemHint: 'System message — rarely used for campaigns',
    audienceGridTitle: 'Households',
    audienceGridEmpty: 'No households match these filters yet.',
    audienceSearch: 'Search log number or name',
    audienceAdd: 'Include',
    audienceRemove: 'Exclude',
    audienceUndo: 'Undo',
    audienceFilteredOut: 'Not matched by the audience filters',
    audienceHandIncluded: 'Added by you',
    audienceHandExcluded: 'Excluded by you',
    audienceGateNote:
      'A household you add still has to have a usable contact for a planned channel. The Reason column says why one is out.',
    // The built-up AND, in words, so the effect of each selection is visible.
    logicLabel: 'Selection',
    logicNone: 'Every household at this property.',
    logicJoin: ' AND ',
    logicAnyOf: (label: string, values: string[]): string =>
      `${label} is ${values.join(' or ')}`,
    logicManyOf: (label: string, values: string[]): string =>
      `${label} is any of ${values.join(', ')}`,
    logicIs: (label: string, value: string): string => `${label} is ${value}`,
    logicFilterOn: (label: string): string => label,
    // Which channels the number above assumes.
    channelsAssumed: (labels: string[]): string =>
      labels.length === 0
        ? 'No channel chosen yet — this count includes every household we can reach on any channel. Choose channels next to narrow it.'
        : `Counted for ${labels.join(' then ')}. Changing the channels changes this count.`,
    // Grid: which cohort a row came from, so the three cases never blur.
    gridSource: 'Source',
    sourceInAudience: 'In audience',
    sourceAdded: 'Added by you',
    sourceFilteredOut: 'Not matched',
    sourceExcluded: 'Excluded by you',
    sourceRuleExcluded: 'Excluded by a rule',
    gridSplit: (
      inAudience: number,
      added: number,
      filteredOut: number,
      ruleExcluded: number
    ): string =>
      `In audience ${inAudience} · Added by you ${added} · Not matched ${filteredOut}` +
      (ruleExcluded > 0 ? ` · Excluded ${ruleExcluded}` : ''),
    // Content: the curated call-goal panel.
    voiceGoalPanelTitle: 'What this call does',
    voiceGoalReadOnly: 'Set by Pronto. A campaign chooses the goal; it cannot change it.',
    voiceGoalChoose: 'Choose a call goal to see what the call does.',
    voiceGoalOpening: 'Opening line',
    voiceGoalScript: 'How the call goes',
    voiceGoalActions: 'What the renter can do on this call',
    voiceGoalEveryCall:
      'Every call first confirms who answered, and says nothing about the file until it has.',
    voiceGoalLimits: 'What it does not do',
    reviewChannels: 'Channels',
    reviewCallGoal: 'Call goal',
    reviewExcluded: 'Excluded',
    reviewExcludedNone: 'Everyone matched is included.',
    reviewExcludedLine: (count: number, reason: string): string =>
      `${count} ${count === 1 ? 'household' : 'households'} — ${reason}`,
    reviewTemplate: 'Template',
    reviewNoTemplate: 'No template — custom content',
    reviewAudience: 'Audience',
    reviewContentMissing:
      'Add content for every planned channel before sending.',
  },
  run: {
    cancel: 'Cancel Campaign',
    retry: 'Retry',
    edit: 'Edit Campaign',
    delete: 'Delete Campaign',
    deleteTitle: 'Delete Campaign?',
    deleteBody:
      'This draft and its settings are removed. Nothing has been sent.',
    keepDraft: 'Keep Draft',
    confirmDelete: 'Delete Draft',
    cancelTitle: 'Cancel Campaign?',
    cancelBody:
      'Recipients already contacted stay contacted. Nothing new will be sent.',
    keepRunning: 'Keep Running',
    confirmCancel: 'Cancel Campaign',
    delivering: 'Delivering',
    // CAMP-007 — Delivering is a timed wait for delivery evidence, not a send in
    // flight. Say so, or the screen reads as "nothing happened".
    waitingForEvidence: (until: string): string =>
      `Waiting for delivery evidence until ${until}. Email has no instant delivery receipt, so the campaign settles then.`,
    waitingForEvidenceUnknown:
      'Waiting for delivery evidence. Email has no instant delivery receipt, so the campaign settles when the delivery window closes.',
    stepNotStarted: 'Not started',
    summary: 'Run details',
    showSummary: 'Show details',
    hideSummary: 'Hide details',
    failedTitle: 'This campaign failed',
    partialTitle: 'Some recipients were not reached',
    cancelledTitle: 'This campaign was cancelled',
    draftTitle: 'This draft has not been sent.',
    draftBody: 'Continue where you left off, or delete it.',
    livePaused: 'Live updates paused. Reconnecting…',
    previewedResolved: (previewed: number, resolved: number): string =>
      `Previewed ${previewed} · resolved ${resolved}`,
    notReached: (count: number): string =>
      `${count} recipient(s) were not reached.`,
    stats: {
      title: 'Recipients',
      reached: 'Reached',
      pending: 'Pending',
      notReached: 'Not reached',
      excluded: 'Excluded',
      aria: 'Recipient outcome breakdown',
    },
  },
  recipients: {
    title: 'Recipients',
    recipient: 'Recipient',
    logNumber: 'Log #',
    reach: 'Reach',
    reason: 'Reason',
    certification: 'File',
    retryOn: (channel: string): string => `Retry on ${channel}`,
    filters: {
      all: 'All',
      notReached: 'Not reached',
      bounced: 'Bounced',
      noAnswer: 'No answer',
      excluded: 'Excluded',
    },
    empty: 'No recipients yet.',
    truncated: 'Showing the first 25,000 recipients. Export for the full list.',
    search: 'Search recipients',
  },
  tray: {
    title: 'Active campaigns',
    paused: 'Live updates paused',
  },
  errors: {
    load: 'Could not load campaigns. Try again.',
    save: 'Could not save the campaign. Check the highlighted fields.',
    preview: 'Could not preview audience. Try again.',
    rows: 'Could not load the households. Try again.',
    cancel: 'Could not cancel the campaign. Try again.',
    retry: 'Could not retry the campaign. Try again.',
    retryRecipient: 'Could not retry this recipient.',
    export: 'Could not export the campaign. Try again.',
    send: 'Could not send the campaign. Check the details and try again.',
    delete: 'Could not delete the campaign. Try again.',
    templates: 'Could not load templates.',
  },
};

export default CAMPAIGNS_COPY;
